// ============================================================
// BrandingCard.js — CareVoice survey branding per client.
// PUT is multipart (logo file + primaryColour form field) — uses
// fetch directly because apiClient is JSON-only.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import Field from '../../components/Field';
import { toast } from '../../components/Toaster';
import { apiRequest } from '../../authConfig';
import { getClientBranding, clientBrandingPath } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

export default function BrandingCard({ clientId, clientName }) {
  const { instance, accounts } = useMsal();
  const [current, setCurrent] = useState(null);
  const [primaryColour, setPrimaryColour] = useState('#3B9396');
  const [accentColour, setAccentColour] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientBranding(clientId);
      setCurrent(res);
      if (res.primaryColour) setPrimaryColour(res.primaryColour);
      // Hydrate BOTH, so a save round-trips what is already stored rather
      // than sending blanks over it (the writer treats an empty string as a
      // deliberate clear).
      setAccentColour(res.accentColour || '');
      setWelcomeMessage(res.welcomeMessage || '');
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  function handleFileChange(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error('Logo must be 2MB or smaller');
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(f.type)) {
      toast.error('Logo must be JPG or PNG');
      return;
    }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  }

  async function submit(e) {
    e.preventDefault();
    if (!logoFile && !current?.logoUrl) {
      toast.error('A logo is required to save branding.');
      return;
    }
    setSaving(true);
    try {
      // Acquire token directly — apiClient is JSON-only.
      const account = accounts[0];
      if (!account) throw new Error('Not signed in');
      const result = await instance.acquireTokenSilent({ ...apiRequest, account });
      const token = result.accessToken;

      const fd = new FormData();
      if (logoFile) fd.append('logo', logoFile);
      fd.append('primaryColour', primaryColour);
      fd.append('accentColour', accentColour);
      fd.append('welcomeMessage', welcomeMessage);

      const url = `${API_BASE}${clientBrandingPath(clientId)}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const text = await res.text();
      let parsed = null;
      if (text) { try { parsed = JSON.parse(text); } catch { parsed = { raw: text }; } }
      if (!res.ok) {
        toast.error(parsed?.error ? translateError({ message: parsed.error }) : `Save failed (${res.status})`);
        return;
      }
      toast.success('Branding saved');
      setLogoFile(null); setLogoPreview(null);
      await fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  const displayLogo = logoPreview || current?.logoUrl || null;

  return (
    <form className="pc-branding" onSubmit={submit}>
      <div>
        <Field label="Current logo">
          <div className="pc-branding__current-logo">
            {displayLogo ? (
              <>
                <img src={displayLogo} alt="Client logo" />
                {current && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                    {current.logoFilename}{current.logoSizeBytes ? ` · ${Math.round(current.logoSizeBytes / 1024)}KB` : ''}
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: '#888', fontSize: 13 }}>No logo yet</div>
            )}
          </div>
        </Field>
        <Field label="Replace logo" help="JPG / JPEG / PNG, max 2 MB.">
          <input type="file" accept="image/jpeg,image/png" onChange={handleFileChange} />
        </Field>
        <Field label="Primary colour" help="Used on the survey header bar.">
          <input type="color" value={primaryColour} onChange={(e) => setPrimaryColour(e.target.value)} />
        </Field>
        {/* NO accent-colour control, deliberately. `BrandAccentColor` is
            stored but NOTHING renders it — SurveyPage applies primaryColour
            to the header, section headings, submit button and radios, and
            reads accent nowhere. A control for it would be a setting that
            does nothing. It is still round-tripped in the payload below so a
            save cannot destroy the value a seed script wrote. Add the
            control at the same time as a consumer, never before. */}
        <Field
          label="Welcome message"
          help="Optional. Shown under the logo on the survey page. Up to 2000 characters."
        >
          <textarea
            rows={4}
            maxLength={2000}
            value={welcomeMessage}
            placeholder="Thank you for taking the time to tell us how we are doing…"
            onChange={(e) => setWelcomeMessage(e.target.value)}
          />
        </Field>
        {current?.clientSlug && (
          <Field label="Survey link" help="Respondents see this address. It follows the client name and is set automatically.">
            <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
              surveys.a2zcarehub.co.uk/survey/{current.clientSlug}/…
            </code>
          </Field>
        )}
        <div className="pc-actions">
          <button type="submit" className="pc-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save branding'}
          </button>
        </div>
      </div>
      <div>
        <Field label="Survey page preview">
          <div className="pc-branding__preview" style={{ borderTop: `4px solid ${primaryColour}` }}>
            {displayLogo && <img src={displayLogo} alt="" />}
            <span style={{ color: primaryColour, fontWeight: 700, fontSize: 16 }}>{clientName}</span>
          </div>
        </Field>
        <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          Approximate. The live survey header will scale the logo and apply additional layout polish.
        </p>
      </div>
    </form>
  );
}
